import { ExternalLink, Eye, ArrowUpRight, Sparkles } from 'lucide-react'
import { translations } from '@/lib/translations'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Autoplay, Pagination, Navigation } from 'swiper/modules'

// Import Swiper styles
import 'swiper/css'
import 'swiper/css/pagination'
import 'swiper/css/navigation'

import { useEffect, useState } from 'react'
import { fetchList, mediaUrl } from '@/lib/api'

export default function PortfolioSection({ language }) {
  const t = translations[language]
  const [projects, setProjects] = useState([])

  useEffect(() => {
    fetchList('/portfolio')
      .then((rows) => setProjects(rows.map((row) => ({
        id: row.id,
        company: row.companyName,
        projectName: row.title,
        image: mediaUrl(row.image),
        link: row.projectUrl || '#',
      }))))
      .catch(() => setProjects([]))
  }, [])

  return (
    <section id="portfolio" className=" px-4">
      <div className="max-w-7xl mx-auto py-2">
       <div className="text-center mb-4 md:mb-4">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/5 border border-primary/10 mb-6 backdrop-blur-sm">
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-primary/80 uppercase tracking-wide">
              Our Portfolio
            </span>
          </div>

          {/* Title */}
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            <span className="bg-gradient-to-r from-primary via-primary/80 to-secondary bg-clip-text text-transparent">
              {t.portfolio.title}
            </span>
          </h2>

          {/* Animated Underline */}
          <div className="relative flex justify-center mb-6">
            <div className="w-24 h-1 bg-gradient-to-r from-primary/40 via-primary to-primary/40 rounded-full" />
            <div className="absolute w-12 h-1 bg-primary rounded-full animate-pulse" />
          </div>

          {/* Subtitle */}
          
        </div>

        <Swiper
          modules={[Autoplay, Pagination, Navigation]}
          spaceBetween={24}
          slidesPerView={1}
          navigation
          pagination={{ clickable: true }}
          autoplay={{ delay: 4000, disableOnInteraction: false }}
          breakpoints={{
            640: { slidesPerView: 2 },
            1024: { slidesPerView: 3 },
            1280: { slidesPerView: 4 },
          }}
          className="pb-14" // Space for pagination dots
        >
          {projects.map((project) => (
            <SwiperSlide key={project.id} className="h-full mb-8">
              <div className="group h-full rounded-lg overflow-hidden bg-card border border-border hover:border-primary transition-all flex flex-col">
                {/* IMAGE */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.company}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>

                {/* CONTENT */}
                <div className="p-4 flex-grow flex flex-col justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">
                      {project.company}
                    </p>
                    <h3 className="font-semibold text-foreground mb-3">
                      {project.projectName}
                    </h3>
                  </div>

                  <a
                    href={project.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-primary hover:gap-3 transition-all text-sm font-medium mt-auto"
                  >
                    {t.portfolio.viewProject}
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </section>
  )
}